import { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { U21Alert } from 'app/shared/u21-ui/components/display/U21Alert';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';
import { U21LoadError } from 'app/shared/u21-ui/components/display/U21LoadError';
import { U21Modal } from 'app/shared/u21-ui/components/layout/modal/U21Modal';
import { U21NoData } from 'app/shared/u21-ui/components/display/U21NoData';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21AiChatInput } from 'app/shared/u21-ui/components/input/chat/U21AiChatInput';
import { U21AiChatMessages } from 'app/shared/u21-ui/components/input/chat/U21AiChatMessages';
import { U21AiChatProps } from 'app/shared/u21-ui/components/input/chat/types';

interface Props extends U21AiChatProps {
  isError?: boolean;
  isLoading?: boolean;
  onRetry: () => void;
  emptyState?: ReactNode;
}

export const U21AiChat = ({
  agentName,
  agentPicture,
  disabled = false,
  emptyState,
  hasMoreOlder,
  isError = false,
  isFetchingOlder,
  isInputShown = true,
  isLoading = false,
  isPending,
  isSendDisabled = false,
  messages,
  onCancel,
  onFetchOlder,
  onRemoveUpload,
  onRetry,
  onSend,
  onUploadFile,
  placeholder,
  sendDisabledTooltip,
  uploads,
}: Props) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0);
  const triggerScrollToBottom = useCallback(
    () => setScrollToBottomTrigger((n) => n + 1),
    [],
  );

  const handleCancelConfirm = async () => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      setIsCancelModalOpen(false);
      return;
    }
    setIsCancelling(true);
    try {
      await onCancel(lastMessage.id);
      setIsCancelModalOpen(false);
      triggerScrollToBottom();
    } finally {
      setIsCancelling(false);
    }
  };

  const stopButtonTooltip = `${agentName} is already working on a request. You must cancel this message to send a new one.`;

  const renderContent = () => {
    if (isLoading) {
      return (
        <CenteredContent>
          <U21Loading loading />
        </CenteredContent>
      );
    }

    if (isError) {
      return (
        <CenteredContent>
          <U21LoadError label="conversation messages" onTryAgain={onRetry} />
        </CenteredContent>
      );
    }

    if (messages.length === 0) {
      return <CenteredContent>{emptyState ?? <U21NoData />}</CenteredContent>;
    }

    return (
      <U21AiChatMessages
        agentName={agentName}
        agentPicture={agentPicture}
        disabled={disabled}
        hasMoreOlder={hasMoreOlder}
        isFetchingOlder={isFetchingOlder}
        messages={messages}
        onFetchOlder={onFetchOlder}
        scrollToBottomTrigger={scrollToBottomTrigger}
      />
    );
  };

  return (
    <ChatContainer spacing={2}>
      <ContentArea>{renderContent()}</ContentArea>
      {isInputShown && (
        <U21AiChatInput
          disabled={disabled || isError}
          isPending={isPending}
          isSendDisabled={isSendDisabled}
          sendDisabledTooltip={sendDisabledTooltip}
          onRemoveUpload={onRemoveUpload}
          onSend={async (message, ids) => {
            await onSend(message, ids);
            triggerScrollToBottom();
          }}
          onStopClick={() => setIsCancelModalOpen(true)}
          onUploadFile={onUploadFile}
          placeholder={placeholder}
          stopButtonTooltip={stopButtonTooltip}
          uploads={uploads}
        />
      )}
      <U21Modal
        actionButtonProps={{
          children: 'Cancel Work',
          color: 'error',
          loading: isCancelling,
          variant: 'outlined',
        }}
        closeButtonProps={{ children: 'Keep Going' }}
        onAction={handleCancelConfirm}
        onClose={() => setIsCancelModalOpen(false)}
        open={isCancelModalOpen}
        size="small"
        title="Cancel Work"
      >
        <U21Alert color="warning" title="This cannot be undone.">
          Stopping mid process will cancel in-flight progress on the current
          message.
        </U21Alert>
      </U21Modal>
    </ChatContainer>
  );
};

const ChatContainer = styled(U21Spacer)`
  height: 100%;
  overflow: hidden;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const CenteredContent = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
`;
